import { activeGamepad, loadBehavior, rumbleGate, rightStick, iosHaptic } from "./game/input.js";
import { padFriendlyName, padGlyphs } from "./ui/padGlyphs.js";
import { openPadKeyboard, padKeyboardKey } from "./ui/padKeyboard.js";
import "./styles.css";
import { DEFAULT_MAP_ID } from "@neon-purge/shared";
import { api } from "./net/api.js";
import { showScreen, uiRoot } from "./ui/dom.js";
import { authScreen } from "./ui/authScreen.js";
import { menuScreen } from "./ui/menuScreen.js";
import { playScreen } from "./ui/playScreen.js";
import { localScreen } from "./ui/localScreen.js";
import { endLocalSession } from "./game/localPlayers.js";
import { editorScreen } from "./ui/editorScreen.js";
import { cosmeticsScreen } from "./ui/cosmeticsScreen.js";
import { skinEditor } from "./ui/skinEditor.js";
import { settingsScreen } from "./ui/settingsScreen.js";
import { profileScreen } from "./ui/profileScreen.js";
import { leaderboardScreen } from "./ui/leaderboardScreen.js";
// NB: game/practice.ts (the old offline sandbox) is intentionally NOT imported
// — "Practice" runs a solo room now; the dead import shipped ~1000 lines.
import { TUTORIAL_STAGES, setTutorialStage, tutorialStage, consumeStageComplete } from "./game/tutorial.js";
import { shouldWelcome, welcomeModal } from "./ui/welcome.js";
import { createSoloRoom } from "./net/game.js";
import type { RoomSession } from "./game/roomSession.js";
import { friendsScreen } from "./ui/friendsScreen.js";
import { adminScreen } from "./ui/adminScreen.js";
import { startFriendEvents, stopFriendEvents, onFriendEvent, friends } from "./net/friends.js";
import { showInvite, showFriendRequest, clearInvite, showWelcome, clearWelcome } from "./ui/invitePopup.js";
import { joinById } from "./net/game.js";
import { audio } from "./game/audio.js";
import { installKind, onInstallChange, promptInstall, isStandalone, showIosFullscreenInfo } from "./pwa.js";
import { reportClientError } from "./net/clientlog.js";
import { MenuBackground } from "./game/menuBackground.js";
import { installXpCardDemo } from "./ui/xpCard.js";
import { isTvApp } from "./game/renderer.js";
import { installGfxPanel } from "./game/gfxPanel.js";
installGfxPanel(); // F7: graphics tuning panel (lil-gui, lazy-loaded)
import { startPresence } from "./net/presence.js";

const canvas = document.getElementById("game-canvas") as HTMLCanvasElement;

interface BootOverlay {
  set: (progress: number, label: string) => void;
  done: () => void;
}

function createBootOverlay(): BootOverlay {
  const root = document.createElement("div");
  root.className = "boot-loader";
  root.innerHTML = `
    <div class="boot-brand">NEON PURGE</div>
    <div class="boot-copy">VALIDATING BUILD</div>
    <div class="boot-track"><div class="boot-fill"></div></div>
    <div class="boot-meta">0%</div>`;
  document.body.appendChild(root);
  const copy = root.querySelector<HTMLElement>(".boot-copy")!;
  const fill = root.querySelector<HTMLElement>(".boot-fill")!;
  const meta = root.querySelector<HTMLElement>(".boot-meta")!;
  return {
    set(progress, label) {
      const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
      copy.textContent = label;
      fill.style.width = `${pct}%`;
      meta.textContent = `${pct}%`;
    },
    done() {
      root.classList.add("done");
      window.setTimeout(() => root.remove(), 220);
    },
  };
}

function assetSig(doc: Document): string {
  return [...doc.querySelectorAll<HTMLScriptElement | HTMLLinkElement>("script[src],link[href][rel='stylesheet'],link[href][rel='modulepreload']")]
    .map((node) => (node instanceof HTMLScriptElement ? node.src : node.href).replace(location.origin, ""))
    .filter((href) => href.startsWith("/assets/"))
    .sort()
    .join("|");
}

async function clearBrowserCaches(): Promise<void> {
  if (!("caches" in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((k) => caches.delete(k)));
}

async function validateFreshLaunch(): Promise<void> {
  const current = assetSig(document);
  if (!current) return;
  const r = await fetch(`/?np_fresh=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) return;
  const latest = assetSig(new DOMParser().parseFromString(await r.text(), "text/html"));
  if (!latest || latest === current) return;
  const key = `np_reloaded_${current}_${latest}`;
  if (sessionStorage.getItem(key) === "1") return;
  sessionStorage.setItem(key, "1");
  location.replace(`/?np_reload=${Date.now()}`);
  await new Promise<never>(() => { /* navigation owns the rest */ });
}

async function preloadGameAssets(bootUi: BootOverlay): Promise<void> {
  const step = async (progress: number, label: string, work: () => void | Promise<void>): Promise<void> => {
    bootUi.set(progress, label);
    try {
      await work();
    } catch (err) {
      reportClientError(`boot preload failed at ${label}: ${String(err)}`, (err as Error)?.stack);
    }
  };

  await step(0.46, "LOADING GAME CODE", async () => {
    await roomSessionLoad;
  });
  // MAP TEXTURES (~5.8 s, the single biggest family) are NO LONGER warmed here:
  // no menu screen renders one (the menu background is avatar-only), so they
  // moved to warmDeferredTextures() on idle — same treatment as monster/prop.
  // The map picker (previews.ts) and every match load (renderer.precompile) call
  // mapTextures(), the force-all backstop, so a family is always complete before
  // it is read; a player faster than the idle pump just pays the (already
  // preview/load-screen-absorbed) cost there, never mid-fight.
  await step(0.66, "WARMING AVATARS", async () => {
    const avatarTex = await import("./game/avatarTextures.js");
    avatarTex.warmAvatarTextures();
  });
  await step(0.74, "WARMING VEHICLES", async () => {
    const vehicleTex = await import("./game/vehicleTextures.js");
    vehicleTex.prewarmVehicleTextures();
  });
  await step(0.82, "WARMING SKY", async () => {
    const skyTex = await import("./game/skyTextures.js");
    skyTex.prewarmSkyTextures();
  });
  await step(0.87, "WARMING WEAPONS", async () => {
    const weaponTex = await import("./game/weaponTextures.js");
    weaponTex.prewarmWeaponTextures();
  });
  // The Play screen's 3D preview (pooled GL context + map build + async shader
  // compile): done HERE, under the boot overlay, not on menu idle — on idle it
  // was one ~0.9 s block the moment the menu appeared, and on first Play click
  // it was ~1.6 s (agent/bench-menu-perf-live.mjs). Awaited compile is async
  // (parallel-compile), so the overlay keeps animating meanwhile.
  await step(0.92, "WARMING MAP PREVIEW", async () => {
    const { warmMapPreview } = await import("./ui/mapPreview.js");
    await warmMapPreview();
  });
  // MONSTER / PROP / COMBAT-FX / CREATURE families are deliberately NOT warmed
  // here: no menu screen renders them (the menu background is avatar-only), and
  // they dominate the boot cost (monster ~3 s + prop ~1.6 s under swiftshader).
  // They are pushed to warmDeferredTextures(), which runs on idle time once the
  // menu is interactive — see the comment there for the correctness backstops.
}

let deferredWarmDone = false;
/** Warm the MID-MATCH-only texture families during idle time AFTER the menu is
 *  interactive, instead of on the blocking boot path — this clears the boot
 *  overlay seconds sooner. Safe because nothing on any menu screen uses these,
 *  and every family already has a pre-first-frame backstop: renderer.precompile()
 *  forces monster/prop/combat/decal under the match load screen, and
 *  ambientLife's constructor forces the creature skins at map load. So a player
 *  who reaches a match before this finishes pays the (already load-screen-
 *  absorbed) cost there — never mid-fight. The two big generators (monster,
 *  prop) are pumped ONE family per idle slice (~300 ms) so the menu never janks
 *  on a multi-second block. All generators are memoised + idempotent. */
async function warmDeferredTextures(): Promise<void> {
  if (deferredWarmDone) return;
  deferredWarmDone = true;
  const w = window as unknown as {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
  };
  const idle: (cb: () => void) => void =
    typeof w.requestIdleCallback === "function"
      ? (cb) => w.requestIdleCallback!(cb, { timeout: 2000 })
      : (cb) => { window.setTimeout(cb, 120); };
  try {
    const [mapTex, monsterTex, propTex] = await Promise.all([
      import("./game/mapTextures.js"),
      import("./game/monsterTextures.js"),
      import("./game/propTextures.js"),
    ]);
    // A per-family task re-queues itself (returns true) until its set is warm,
    // capping any single idle slice at ~one family; the small single-shot
    // groups run once (return false). MAP families go FIRST — the map picker is
    // the first post-menu screen that needs them, and finishing them early
    // shrinks the window where clicking Play force-builds the remainder.
    const tasks: Array<() => boolean> = [
      () => mapTex.warmOneMapFamily(),
      () => { mapTex.roadPaintTexture(); return false; },
      () => monsterTex.warmOneMonsterFamily(),
      () => propTex.warmOnePropFamily(),
      () => { void warmCombatFx(); return false; },
      () => { void warmCreatureSkins(); return false; },
    ];
    let i = 0;
    const pump = (): void => {
      if (i >= tasks.length) return;
      let again = false;
      try { again = tasks[i](); } catch (err) {
        reportClientError(`idle warm failed at task ${i}: ${String(err)}`, (err as Error)?.stack);
      }
      if (!again) i++;
      if (i < tasks.length) idle(pump);
    };
    idle(pump);
  } catch (err) {
    reportClientError(`idle warm failed to start: ${String(err)}`, (err as Error)?.stack);
  }
}

async function warmCombatFx(): Promise<void> {
  const [combatTex, decalTex, fxTex] = await Promise.all([
    import("./game/combatTextures.js"),
    import("./game/decalTextures.js"),
    import("./game/fxTextures.js"),
  ]);
  combatTex.prewarmCombatTextures();
  decalTex.prewarmDecalTextures();
  fxTex.streamTexture();
  fxTex.splashTexture();
  fxTex.rippleNormalTexture();
  fxTex.dropletGeometry();
}

async function warmCreatureSkins(): Promise<void> {
  const procTex = await import("./game/procTextures.js");
  procTex.furTexture();
  procTex.hideTexture();
  procTex.scaleTexture();
}

// ---- PHONE GATE: the game needs a computer or a tablet. Phones (mobile UA
// or a touch device with a small screen) get a friendly blocker instead of a
// broken cramped experience. Tablets pass: iPads report desktop-Mac UA +
// touch with a big screen; Android tablets lack the "Mobile" token.
(() => {
  const ua = navigator.userAgent;
  const phoneUa = /iPhone|iPod|Windows Phone/i.test(ua) || (/Android/i.test(ua) && /Mobile/i.test(ua));
  const tinyTouch = (navigator.maxTouchPoints ?? 0) > 0 && Math.min(screen.width, screen.height) < 600;
  if (!phoneUa && !tinyTouch) return;
  // MOBILE BETA: phones are welcome now (touch controls + optional aim assist) —
  // this is a one-tap welcome, not a blocker. The tap doubles as the user
  // gesture for fullscreen + landscape.
  const gate = document.createElement("div");
  gate.style.cssText = "position:fixed;inset:0;z-index:99999;background:#05060a;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;padding:2rem;gap:1rem;font-family:monospace";
  gate.innerHTML = `
    <div style="font-size:3rem">📱</div>
    <div style="font-size:1.6rem;color:#00f0ff;text-shadow:0 0 12px rgba(0,240,255,0.8);letter-spacing:0.12em">NEON PURGE</div>
    <div style="color:#9dff00;letter-spacing:0.2em;font-size:0.9rem">MOBILE BETA</div>
    <div style="color:#e8edf5;max-width:26rem;line-height:1.5">Left thumb <b style="color:#00f0ff">moves</b>, right thumb <b style="color:#00f0ff">looks</b>. Rotate to <b style="color:#9dff00">landscape</b> for the real thing.</div>
    <div style="color:#8a95a5;font-size:0.8rem">Best experienced on computer — but the fight fits in your pocket now.</div>`;
  const playBtn = document.createElement("button");
  playBtn.textContent = "▶ PLAY (BETA)";
  playBtn.style.cssText = "margin-top:1rem;background:rgba(0,240,255,0.15);border:1px solid #00f0ff;color:#00f0ff;padding:0.8rem 2rem;border-radius:6px;font-family:monospace;font-size:1rem;letter-spacing:0.1em";
  playBtn.onclick = () => {
    gate.remove();
    // best-effort immersion — some browsers refuse either, that's fine
    void document.documentElement.requestFullscreen?.().catch(() => { /* denied */ });
    (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })?.lock?.("landscape").catch(() => { /* unsupported */ });
  };
  gate.appendChild(playBtn);
  // "get the real app" right on the welcome gate — adds to the home screen
  const gateInstall = document.createElement("button");
  gateInstall.textContent = "📲 INSTALL THE APP";
  gateInstall.style.cssText = "background:rgba(157,255,0,0.12);border:1px solid #9dff00;color:#9dff00;padding:0.6rem 1.6rem;border-radius:6px;font-family:monospace;font-size:0.85rem;letter-spacing:0.1em;display:none";
  gateInstall.onclick = () => promptInstall();
  const syncGateInstall = (): void => { gateInstall.style.display = installKind() ? "block" : "none"; };
  onInstallChange(syncGateInstall);
  syncGateInstall();
  gate.appendChild(gateInstall);
  document.body.appendChild(gate);
})();

// ---- MOBILE: landscape-only. A CSS media query shows this overlay whenever
// a touch device is held in portrait (menus AND game). Also re-attempt the
// orientation lock on any tap — it only works once fullscreen is granted.
(() => {
  if ((navigator.maxTouchPoints ?? 0) === 0 || !window.matchMedia("(pointer: coarse)").matches) return;
  const rg = document.createElement("div");
  rg.className = "rotate-gate";
  rg.innerHTML = `
    <div class="rg-icon">📱</div>
    <div class="rg-title">ROTATE YOUR PHONE</div>
    <div class="rg-sub">NEON PURGE is played in landscape</div>`;
  document.body.appendChild(rg);
  // small self-dismissing toast so fullscreen failures are VISIBLE (they
  // were silent — "the button does not work" with no way to tell why)
  let toastEl: HTMLElement | null = null;
  let toastTimer = 0;
  const toast = (msg: string): void => {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.style.cssText = "position:fixed;left:50%;bottom:calc(18px + env(safe-area-inset-bottom, 0px));transform:translateX(-50%);z-index:100002;" +
        "background:rgba(8,11,20,0.92);border:1px solid #00f0ff;color:#e8edf5;padding:0.5rem 1rem;" +
        "border-radius:8px;font-family:monospace;font-size:0.8rem;max-width:88vw;text-align:center";
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.style.display = "block";
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { if (toastEl) toastEl.style.display = "none"; }, 4000);
  };

  // phones have no devtools — surface uncaught errors as toasts AND beacon
  // them to the api logs, so field reports carry the actual exception
  window.addEventListener("error", (e) => {
    toast(`⚠ ${e.message}`);
    reportClientError(`uncaught: ${e.message} @ ${e.filename?.split("/").pop() ?? "?"}:${e.lineno}`, e.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const msg = (e.reason as Error)?.message ?? String(e.reason);
    toast(`⚠ ${msg}`);
    reportClientError(`unhandled rejection: ${msg}`, (e.reason as Error)?.stack);
  });

  // iPhone Safari has NO fullscreen API at all — there the only route to a
  // bar-less fullscreen is installing to the home screen, so the ⛶ button
  // falls through to the install walkthrough instead of silently no-opping.
  const docEl = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => void };
  const fsSupported = typeof docEl.requestFullscreen === "function" || typeof docEl.webkitRequestFullscreen === "function";
  const goFullscreen = (loud = false): void => {
    if (!document.fullscreenElement) {
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen().catch((err: Error) => {
          if (loud) toast(`fullscreen refused by the browser: ${err?.message ?? err}`);
        });
      } else if (docEl.webkitRequestFullscreen) {
        try { docEl.webkitRequestFullscreen(); } catch (err) {
          if (loud) toast(`fullscreen refused by the browser: ${(err as Error).message}`);
        }
      }
    }
    (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })?.lock?.("landscape").catch(() => { /* unsupported */ });
  };
  // EVERY tap re-attempts while not fullscreen (lightly throttled) — the
  // browser only honors it inside a gesture, so we take every gesture we get
  let lockTried = 0;
  document.addEventListener("touchend", () => {
    const now = Date.now();
    if (document.fullscreenElement || now - lockTried < 1200) return;
    lockTried = now;
    goFullscreen();
  }, { passive: true });

  // TAP RELIABILITY: commit real <button> taps on touchend ourselves and
  // suppress the browser's synthesized click — on some mobile browsers the
  // synthesized click intermittently never arrives (the "Resume highlights
  // but does nothing" bug). Drag-guarded so scrolling over a button is safe.
  let tapBtn: HTMLButtonElement | null = null, tapX = 0, tapY = 0;
  document.addEventListener("touchstart", (e) => {
    tapBtn = (e.target as HTMLElement | null)?.closest?.("button") ?? null;
    tapX = e.touches[0]?.clientX ?? 0;
    tapY = e.touches[0]?.clientY ?? 0;
  }, { passive: true, capture: true });
  document.addEventListener("touchend", (e) => {
    const b = tapBtn;
    tapBtn = null;
    if (!b || b.disabled || !b.isConnected) return;
    const t = e.changedTouches[0];
    if (!t || Math.hypot(t.clientX - tapX, t.clientY - tapY) > 14) return; // drag, not a tap
    if (((e.target as HTMLElement | null)?.closest?.("button") ?? null) !== b) return; // slid off it
    e.preventDefault(); // kills the (unreliable) synthesized click — we fire our own
    // MENU/UI HAPTIC: a light tick on every button tap. Android gets the
    // Vibration API; iOS has no such API, but this IS a genuine user gesture, so
    // the <input switch> system-haptic trick actually fires here (it can't from
    // game events). The in-game touch controls are <div>s, so they're unaffected.
    if (typeof navigator.vibrate === "function") { try { navigator.vibrate(8); } catch { /* refused */ } }
    else iosHaptic();
    b.click();
  }, { passive: false, capture: true });

  // persistent ⛶ button whenever fullscreen is NOT engaged (top center —
  // the mobile HUD hides the hint bar there), + 📲 INSTALL when the browser
  // offers the PWA prompt (installed = true fullscreen forever)
  const standalone = isStandalone();
  const mkTopBtn = (text: string, right: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.textContent = text;
    b.style.cssText = `position:fixed;top:calc(6px + env(safe-area-inset-top, 0px));right:${right};z-index:99998;` +
      "background:rgba(8,11,20,0.8);border:1px solid #00f0ff;color:#00f0ff;padding:0.35rem 0.7rem;" +
      "border-radius:6px;font-family:monospace;font-size:0.75rem;letter-spacing:0.08em;display:none";
    document.body.appendChild(b);
    return b;
  };
  const fsBtn = mkTopBtn("⛶ FULLSCREEN", "8px");
  fsBtn.classList.add("top-fs-btn"); // hidden in-game via body.np-ingame (offered in the pause menu instead)
  // browsers with the API go fullscreen (loud = failures show a toast);
  // iPhone has no API → explain why + point at the home-screen install
  fsBtn.onclick = () => { if (fsSupported) goFullscreen(true); else showIosFullscreenInfo(); };
  // in-game the class-rule (!important) hides it regardless of this inline style
  const syncFs = (): void => { fsBtn.style.display = document.fullscreenElement || standalone ? "none" : "block"; };
  for (const evt of ["fullscreenchange", "webkitfullscreenchange"]) {
    document.addEventListener(evt, () => {
      syncFs();
      // re-assert landscape whenever fullscreen (re)engages
      if (document.fullscreenElement) (screen.orientation as unknown as { lock?: (o: string) => Promise<void> })?.lock?.("landscape").catch(() => { /* ok */ });
    });
  }
  syncFs();

  const installBtn = mkTopBtn("📲 INSTALL", "128px");
  installBtn.classList.add("top-install-btn"); // hidden in-game via body.np-ingame
  const syncInstall = (): void => { installBtn.style.display = installKind() ? "block" : "none"; };
  onInstallChange(syncInstall);
  syncInstall();
  installBtn.onclick = () => promptInstall();
})();

// PWA: register the (no-cache) service worker so browsers offer
// "Install NEON PURGE" — desktop omnibox icon + mobile add-to-home-screen.
// update() forces the browser to re-check sw.js on every load.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").then((reg) => { void reg.update(); }).catch(() => { /* http dev — fine */ });
  });
}

// global sign-in feed: "X jacked in" toasts on every screen (in-match it
// routes into the chat log instead) — stays open for the whole session
startPresence();

// CRT overlay — pure CSS (scanlines + vignette + faint flicker). GPU-composited,
// costs no render pass, so it never adds frame lag. Sits above everything.
const crt = document.createElement("div");
crt.className = "crt-overlay";
crt.innerHTML = '<div class="crt-scan"></div><div class="crt-vignette"></div>';
document.body.appendChild(crt);

// Live "controller detected" indicator (so you can confirm the browser sees
// your gamepad). It flashes on connect + shows whenever a pad is present.
const padTag = document.createElement("div");
padTag.className = "pad-indicator";
document.body.appendChild(padTag);
// Flashes for a few seconds when a NEW pad shows up (Chrome only reports a pad
// after its first press, hence the poll) — the persistent per-frame button
// readout that used to live here is now opt-in (localStorage padDebug=1); the
// player-facing guidance is the button-prompt bar below.
let padTagKey = "";
let padTagHideAt = 0;
const refreshPad = (): void => {
  const gp = activeGamepad();
  const key = gp ? `${gp.index}:${gp.id}` : "";
  if (key && key !== padTagKey) {
    padTag.textContent = `🎮 ${padFriendlyName(gp!)} connected`;
    padTagHideAt = performance.now() + 3500;
  }
  padTagKey = key;
  padTag.classList.toggle("on", !!gp && performance.now() < padTagHideAt);
};
window.addEventListener("gamepadconnected", refreshPad);
window.addEventListener("gamepaddisconnected", refreshPad);
setInterval(refreshPad, 500);

// ---- controller MENU navigation (only active on menu/overlay screens; the
// in-game loop owns the pad during a match) ----
let navPrev: boolean[] = [];
let navPadKey = "";
let padDbgNextAt = 0; // throttle for the 🎮 debug readout (4Hz, not per-RAF)
let navRepeat = 0;
let navLastIdx = -1;
let navTypingEl: HTMLInputElement | HTMLTextAreaElement | null = null;
let navSuppressRestoreUntil = 0;
const TEXT_ENTRY_TYPES = /^(text|email|password|search|tel|url|number)$/;
const isTextEntry = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement =>
  el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && TEXT_ENTRY_TYPES.test(el.type));
const menuPadButtons = (gp: Gamepad): { cross: number; circle: number; square: number; triangle: number } => {
  const psDirect = gp.mapping !== "standard" && /playstation|dualshock|dualsense|wireless controller|sony|054c/i.test(gp.id);
  return psDirect ? { cross: 1, circle: 2, square: 0, triangle: 3 } : { cross: 0, circle: 1, square: 2, triangle: 3 };
};
/** Desktop browsers show NO keyboard for a focused field, so a pad player gets
 *  our on-screen one (ui/padKeyboard.ts). Consoles, phones/tablets and the TV
 *  app pop their own system keyboard on focus — doubling it would be worse. */
const useOnScreenKeyboard = (): boolean => {
  const ua = navigator.userAgent || "";
  if (isTvApp() || /Xbox|PlayStation|Nintendo|Android|iPhone|iPad|Mobile/i.test(ua)) return false;
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return false; // iPadOS desktop UA
  return true;
};
/** The element pad-nav is scoped to: a modal popup (room invite / friend
 *  request — they live on <body>, outside #ui) wins on menus; otherwise the
 *  topmost #ui screen. Modals are NOT pad-driven mid-match (no pause open):
 *  Cross would "Join" while the player meant to fire. */
const menuScope = (): HTMLElement | null => {
  const screens = document.querySelectorAll<HTMLElement>("#ui .screen");
  const top = screens[screens.length - 1] ?? null;
  const modals = document.querySelectorAll<HTMLElement>("[data-pad-modal]");
  const modal = modals[modals.length - 1];
  if (modal && (!document.querySelector(".hud") || top)) return modal;
  return top;
};
const menuFocusables = (): HTMLElement[] => {
  const screen = menuScope();
  if (!screen) return [];
  const nativelyFocusable = /^(BUTTON|INPUT|SELECT|A|TEXTAREA)$/;
  return [...screen.querySelectorAll<HTMLElement>("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex], .skin-slot, .room-row")]
    // a plain <div> (.room-row / .skin-slot wrapper) can't receive focus — its
    // inner buttons already carry the action, so drop the wrapper from the ring
    .filter((el) => nativelyFocusable.test(el.tagName) || el.tabIndex >= 0)
    .filter((el) => el.offsetParent !== null && el.getBoundingClientRect().width > 1);
};
/** Nearest focusable in a direction (dx,dy ∈ {-1,0,1}). Distance along the
 *  travel axis counts once; sideways misalignment costs extra, and a target
 *  that OVERLAPS the current one sideways (a wide button above a row of three)
 *  pays nothing for it — so ↓ from "Play" lands on the button straight below,
 *  and ↓ from a row lands on the next row instead of the row-mate the old
 *  DOM-order walk picked. */
const spatialPick = (els: HTMLElement[], cur: HTMLElement, dx: number, dy: number): HTMLElement | null => {
  const r = cur.getBoundingClientRect();
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  let best: HTMLElement | null = null, bestScore = Infinity;
  for (const e of els) {
    if (e === cur) continue;
    const er = e.getBoundingClientRect();
    const ex = er.left + er.width / 2, ey = er.top + er.height / 2;
    let along: number, gap: number, off: number;
    if (dx !== 0) {
      along = (ex - cx) * dx;
      gap = Math.max(0, Math.max(er.top, r.top) - Math.min(er.bottom, r.bottom));
      off = Math.abs(ey - cy);
    } else {
      along = (ey - cy) * dy;
      gap = Math.max(0, Math.max(er.left, r.left) - Math.min(er.right, r.right));
      off = Math.abs(ex - cx);
    }
    if (along < 12) continue; // must actually be on that side
    const score = along + gap * 3 + off * 0.35;
    if (score < bestScore) { bestScore = score; best = e; }
  }
  return best;
};
/** B / Esc target: an explicit [data-pad-back] wins; otherwise a button that
 *  reads as "go back". NOT "Leave Match" — B on the pause menu used to quit
 *  the match outright; the pause menu now tags Resume instead. */
const BACK_TEXT = /\b(back|cancel|done|close|leave room)\b/i;
const padBackTarget = (els: HTMLElement[]): HTMLElement | undefined =>
  els.find((e) => e.hasAttribute("data-pad-back")) ?? els.find((e) => BACK_TEXT.test(e.textContent || ""));
const START_TEXT = /start|play vs|play online|quick play/i;
const padStartTarget = (els: HTMLElement[]): HTMLElement | undefined =>
  els.find((e) => e.hasAttribute("data-pad-start")) ?? els.find((e) => START_TEXT.test(e.textContent || ""));
/** Where the ring lands when a screen opens: [data-pad-default], else the
 *  primary call-to-action, else the first control that isn't a text field
 *  (landing on an input would pop the console keyboard on the next A). */
const padDefaultTarget = (els: HTMLElement[]): HTMLElement | undefined =>
  els.find((e) => e.hasAttribute("data-pad-default"))
  ?? els.find((e) => e.classList.contains("primary") && !isTextEntry(e))
  ?? els.find((e) => !isTextEntry(e))
  ?? els[0];

// Button-prompt bar: bottom-centre legend in the player's controller glyphs,
// shown only on menus while a pad is in use. Rebuilt only when its text key
// changes (it is evaluated per frame).
const padHints = document.createElement("div");
padHints.className = "pad-hints";
document.body.appendChild(padHints);
let padHintsKey = "";
const setPadHints = (items: [string, string][] | null): void => {
  const key = items ? items.map((i) => i.join(":")).join("|") : "";
  if (key === padHintsKey) return;
  padHintsKey = key;
  padHints.classList.toggle("on", !!items);
  if (!items) return;
  padHints.replaceChildren(...items.map(([glyph, label]) => {
    const item = document.createElement("span");
    item.className = "pad-hint";
    const g = document.createElement("b");
    g.className = "pad-glyph";
    g.textContent = glyph;
    item.append(g, label);
    return item;
  }));
};
const padDebug = (() => { try { return localStorage.getItem("padDebug") === "1"; } catch { return false; } })();
// body.pad-nav while the controller is driving the menu (a mouse move hands
// it back) — the CSS uses it to turn the focus ring into the big animated
// console-style highlight instead of the desktop one
let padNavActive = false;
const setPadNavActive = (on: boolean): void => {
  if (on === padNavActive) return;
  padNavActive = on;
  document.body.classList.toggle("pad-nav", on);
};
window.addEventListener("mousemove", (e) => {
  // a REAL hand on the mouse only — Chrome's synthetic post-scroll moves carry
  // no movement and would otherwise drop pad mode on every right-stick scroll
  if (Math.abs(e.movementX) + Math.abs(e.movementY) > 2) setPadNavActive(false);
}, { passive: true });
let navScope: HTMLElement | null = null;
let navDirHeld = false;
let navHeldFrames = 0;

const padMenuLoop = (): void => {
  requestAnimationFrame(padMenuLoop);
  const ae = document.activeElement as HTMLElement | null;
  const gp = activeGamepad();
  const gpKey = gp ? `${gp.index}:${gp.id}` : "";
  if (gpKey !== navPadKey) {
    navPadKey = gpKey;
    navPrev = []; // edges from the old/ghost pad must not suppress the real pad
    navRepeat = 0;
  }
  if (!gp) setPadNavActive(false);
  if (gp && navTypingEl && (!navTypingEl.isConnected || document.activeElement !== navTypingEl)) navTypingEl = null;
  if (gp && navTypingEl && isTextEntry(ae)) {
    // Explicit typing mode: Cross enters/reopens the console text picker, Circle
    // exits typing. A focused input alone remains navigable by the stick.
    const padBtns = menuPadButtons(gp);
    const edge = (i: number): boolean => !!gp!.buttons[i]?.pressed && !navPrev[i];
    const g = padGlyphs(gp);
    setPadHints([[g.confirm, "Keyboard"], [g.back, "Done typing"]]);
    if (edge(padBtns.cross)) {
      ae.focus({ preventScroll: true });
      ae.click();
    }
    if (edge(padBtns.circle)) {
      navTypingEl = null;
      navSuppressRestoreUntil = performance.now() + 300;
      ae.blur();
    }
    navPrev = gp.buttons.map((b) => b.pressed);
    return;
  }
  const els = gp ? menuFocusables() : [];
  // opt-in raw readout for mapping bugs (localStorage padDebug=1) — ~4Hz, only
  // when the string changes (axis noise made this a per-RAF DOM write)
  if (padDebug && gp && els.length > 0 && performance.now() >= padDbgNextAt) {
    padDbgNextAt = performance.now() + 250;
    const pressed = gp.buttons.map((b, i) => (b.pressed ? i : -1)).filter((i) => i >= 0);
    const rsDbg = rightStick(gp);
    const txt = `🎮 btn[${pressed.join(",")}] LS ${(gp.axes[0] ?? 0).toFixed(1)},${(gp.axes[1] ?? 0).toFixed(1)} RS ${rsDbg.x.toFixed(1)},${rsDbg.y.toFixed(1)}`;
    if (txt !== padTag.textContent) padTag.textContent = txt;
    padTag.classList.add("on");
  }
  if (!gp || els.length === 0) { navPrev = []; navScope = null; setPadHints(null); return; }
  // A NEW screen/overlay/modal opened: the remembered index belongs to the old
  // one (restoring it landed the ring on an arbitrary control). Start the ring
  // on the screen's primary action instead — immediately, so a pad player
  // never has to "wake" the menu with a press first.
  const scope = menuScope();
  if (scope !== navScope) {
    navScope = scope;
    navLastIdx = -1;
    const def = padDefaultTarget(els);
    // only while the pad is the active input — a pad idling on the desk must
    // not paint a focus ring on a mouse player's menu
    // (a field the player is typing in keeps focus — unless it lies OUTSIDE the
    // new scope, e.g. the on-screen keyboard just opened over it)
    const ae2 = document.activeElement;
    if (def && padNavActive && (!isTextEntry(ae2) || !scope?.contains(ae2))) {
      def.focus({ preventScroll: true });
      navLastIdx = els.indexOf(def);
    }
  }
  // D-pad as a HAT axis (axes[9], 8-way encoded) — many pads report it this
  // way instead of buttons 12-15, which made menus un-navigable on them
  const hat = gp.axes[9] ?? 3.3; // 3.3 ≈ released
  const hatDir = (v: number): { u: boolean; d: boolean; l: boolean; r: boolean } => {
    if (v > 2) return { u: false, d: false, l: false, r: false };
    const step = Math.round((v + 1) / (2 / 7)); // 0..7 clockwise from up
    return {
      u: step === 7 || step === 0 || step === 1,
      r: step >= 1 && step <= 3,
      d: step >= 3 && step <= 5,
      l: step >= 5 && step <= 7,
    };
  };
  const h = hatDir(hat);
  // menu haptics: a tick when the focus moves, a thump when you activate.
  // Respects the Vibration setting; actuator-less pads (Steam ghosts) skipped.
  const buzz = (big: boolean): void => {
    const mult = loadBehavior().rumble ?? 1;
    if (mult <= 0) return;
    // respect the shared priority gate — the A-press thump used to fire right
    // AFTER a launch click and stomp the 2s launch blast within 140ms
    if (!rumbleGate(big ? 1.7 : 1.25, big ? 140 : 80)) return;
    const act = (gp as unknown as { vibrationActuator?: { playEffect?: (t: string, o: object) => Promise<unknown> | void } }).vibrationActuator;
    if (!act?.playEffect) {
      if (big) iosHaptic();
      return;
    }
    try {
      const p = act.playEffect("dual-rumble", big
        ? { duration: 140, weakMagnitude: Math.min(1, 0.9 * mult), strongMagnitude: Math.min(1, 0.8 * mult) }
        : { duration: 80, weakMagnitude: Math.min(1, 0.85 * mult), strongMagnitude: Math.min(1, 0.4 * mult) });
      void Promise.resolve(p).catch(() => { if (big) iosHaptic(); });
    } catch { if (big) iosHaptic(); }
  };
  const { cross: crossButton, circle: circleButton, square: squareButton, triangle: triangleButton } = menuPadButtons(gp);
  const btn = (i: number): boolean => !!gp!.buttons[i]?.pressed;
  const edge = (i: number): boolean => btn(i) && !navPrev[i];
  const crossEdge = (): boolean => edge(crossButton);
  const circleEdge = (): boolean => edge(circleButton);
  const ax = (i: number): number => gp!.axes[i] ?? 0;
  const now = performance.now();
  // Console-style key repeat: the first step is instant, holding waits a beat
  // (so a tap never double-steps), then scrolls quickly through long lists.
  const dirHeld = Math.abs(ax(0)) > 0.55 || Math.abs(ax(1)) > 0.55 ||
    btn(12) || btn(13) || btn(14) || btn(15) || h.u || h.d || h.l || h.r;
  if (!dirHeld) navRepeat = 0;
  const firstStep = dirHeld && !navDirHeld;
  navDirHeld = dirHeld;
  navHeldFrames = dirHeld ? navHeldFrames + 1 : 0;
  // repeat needs the time AND a few frames of hold: on a crawling menu (the TV
  // app, a software-GL box at ~3 fps) one tap spans >340 ms of wall time and
  // would otherwise step twice. At 60 fps the frame gate is never the binding one.
  const rep = now > navRepeat && (navHeldFrames <= 1 || navHeldFrames >= 4);
  const nextRepeat = (): number => now + (firstStep ? 340 : 115);
  if (dirHeld || gp.buttons.some((b, i) => b.pressed && !navPrev[i])) setPadNavActive(true);
  let idx = els.indexOf(document.activeElement as HTMLElement);
  // a screen re-render can replace the focused element mid-navigation (the
  // room lobby refreshes on every state patch) — resume from where we were
  // instead of resetting to the top
  if (idx < 0 && navLastIdx >= 0 && navLastIdx < els.length) idx = navLastIdx;
  // Vertical menu navigation accepts both the left stick and D-pad up/down.
  // D-pad left/right remains the dedicated map cycler on map-select screens.
  const dpadVerticalOnly = !btn(14) && !btn(15) && !h.l && !h.r;
  const dpadDown = dpadVerticalOnly && (btn(13) || h.d) && rep;
  const dpadUp = dpadVerticalOnly && (btn(12) || h.u) && rep;
  const down = (ax(1) > 0.55 && rep) || dpadDown;
  const up = (ax(1) < -0.55 && rep) || dpadUp;
  if (down || up) {
    const step = down ? 1 : -1;
    const from = idx >= 0 ? els[idx] : null;
    // geometric first (grids, rows of buttons), DOM order as the wrap-around
    // fallback when nothing lies further in that direction
    const target = from ? spatialPick(els, from, 0, step) : null;
    if (target) {
      idx = els.indexOf(target);
      target.focus({ preventScroll: false });
    } else {
      // nothing focused yet → the screen's primary action; at the edge → wrap
      const def = from ? null : padDefaultTarget(els);
      idx = def ? els.indexOf(def) : step > 0 ? 0 : els.length - 1;
      let tries = 0;
      els[idx].focus({ preventScroll: false });
      while (document.activeElement !== els[idx] && ++tries < els.length) {
        idx = (idx + step + els.length) % els.length;
        els[idx].focus({ preventScroll: false });
      }
    }
    els[idx].scrollIntoView({ block: "nearest", inline: "nearest" }); // keep the ring on-screen in tall panels
    navRepeat = nextRepeat();
    buzz(false);
  } else if (els.indexOf(document.activeElement as HTMLElement) < 0) {
    // nothing focused (a re-render replaced the node, or first pad touch):
    // once pad-nav has been used, keep the ring alive WITHOUT a fresh press
    if ((navLastIdx >= 0 && now > navSuppressRestoreUntil) || crossEdge()) {
      idx = Math.max(0, Math.min(idx < 0 ? 0 : idx, els.length - 1));
      // preventScroll: restoring the ring after a re-render must NOT yank the
      // page back to the focused button (it fought the right-stick scroll)
      els[idx].focus({ preventScroll: true });
    }
  }
  navLastIdx = idx;
  const cur = idx >= 0 ? els[idx] : null;
  // SPLIT the two horizontal sources: the D-PAD (buttons 14/15 or the hat)
  // and the LEFT STICK are different intents — on the play menu + lobby the
  // d-pad swaps the MAP while the stick keeps navigating.
  const dpadRight = (btn(15) || h.r) && rep;
  const dpadLeft = (btn(14) || h.l) && rep;
  const right = dpadRight || (ax(0) > 0.55 && rep);
  const left = dpadLeft || (ax(0) < -0.55 && rep);
  const mapScope = document.querySelector("[data-pad-cycle-scope]");
  const modeScope = document.querySelector("[data-mode-cycle-scope]");
  if (cur instanceof HTMLInputElement && cur.type === "range" && (left || right)) {
    // sliders keep left/right for value adjustment
    const step = (Number(cur.step) || 1) * (right ? 1 : -1);
    cur.value = String(Number(cur.value) + step);
    cur.dispatchEvent(new Event("input", { bubbles: true }));
    navRepeat = now + (firstStep ? 300 : 60); // held = smooth sweep
  } else if (cur instanceof HTMLSelectElement && (left || right)) {
    // dropdowns (weapon ladder, round time): a pad can't open a NATIVE
    // select — step through the options in place instead
    const n = cur.options.length;
    if (n) {
      cur.selectedIndex = (cur.selectedIndex + (right ? 1 : n - 1) + n) % n;
      cur.dispatchEvent(new Event("change", { bubbles: true }));
      navRepeat = now + (firstStep ? 340 : 200);
      buzz(false);
    }
  } else if (mapScope && (dpadLeft || dpadRight)) {
    // D-PAD ←/→ on the play menu / room lobby = cycle the map (the stick
    // below still does spatial navigation — they don't fight anymore)
    mapScope.querySelector<HTMLButtonElement>(dpadRight ? "[data-cycle-next]" : "[data-cycle-prev]")?.click();
    navRepeat = now + (firstStep ? 340 : 220);
    buzz(false);
  } else if ((left || right) && cur) {
    // SPATIAL left/right: jump focus to the geometrically nearest control in
    // that direction (e.g. hop between the two columns on FIND A MATCH)
    const best = spatialPick(els, cur, right ? 1 : -1, 0);
    if (best) { best.focus(); navRepeat = nextRepeat(); buzz(false); }
  } else if ((left || right) && !cur && els.length) {
    els[0].focus();
    navRepeat = nextRepeat();
  }
  // RIGHT (camera) STICK ↑/↓ anywhere in menus: SCROLL the visible panel.
  // Axis layout autodetected per pad (rest-baseline) — layouts vary wildly.
  const rs = rightStick(gp);
  const rsY = rs.y;
  // wide deadzone: a resting/drifting stick was auto-scrolling the menu down
  if (Math.abs(rsY) > 0.55) {
    // candidates: panels/lists, the screen itself, then the PAGE (the play
    // menu has no scrollable container — the body scrolls when it overflows)
    const scroller = [...document.querySelectorAll<HTMLElement>("#ui .panel, #ui .room-list, #ui [style*='overflow'], #ui .screen")]
      .find((e2) => e2.scrollHeight > e2.clientHeight + 4 && e2.offsetParent !== null)
      ?? ((document.scrollingElement && document.scrollingElement.scrollHeight > window.innerHeight + 4) ? document.scrollingElement as HTMLElement : null);
    scroller?.scrollBy(0, rsY * 16);
  }
  // GAME MODE cycling on the play menu / room lobby: RIGHT STICK ←/→ (the
  // original binding) or LB/RB (the discoverable one the prompt bar shows).
  // Clicks the next/prev button in the tagged mode row.
  const rsX = rs.x;
  const lbEdge = edge(4), rbEdge = edge(5);
  const modeDir = lbEdge ? -1 : rbEdge ? 1 : (Math.abs(rsX) > 0.6 && rep) ? Math.sign(rsX) : 0;
  if (modeScope && modeDir !== 0) {
    const btns = [...modeScope.querySelectorAll<HTMLButtonElement>("button")];
    const cur2 = btns.findIndex((b) => b.classList.contains("active"));
    if (btns.length) {
      const next = btns[(Math.max(0, cur2) + (modeDir > 0 ? 1 : btns.length - 1)) % btns.length];
      next.click();
      navRepeat = now + 260;
      buzz(false);
    }
  } else if (!modeScope && mapScope && (lbEdge || rbEdge)) {
    // no mode row (couch lobby): the bumpers page maps like the d-pad does
    mapScope.querySelector<HTMLButtonElement>(rbEdge ? "[data-cycle-next]" : "[data-cycle-prev]")?.click();
    buzz(false);
  }
  if (crossEdge() && cur && !(cur instanceof HTMLInputElement && cur.type === "range")) { // Cross/A = activate
    if (isTextEntry(cur) && !cur.readOnly && useOnScreenKeyboard()) {
      openPadKeyboard(cur);
    } else if (isTextEntry(cur)) {
      navTypingEl = cur;
      cur.focus({ preventScroll: true });
      cur.click();
    } else if (cur instanceof HTMLSelectElement) {
      // X on a dropdown = cycle to the next option (native pickers are
      // unreachable with a pad); ←/→ also steps through them
      const n = cur.options.length;
      if (n) {
        cur.selectedIndex = (cur.selectedIndex + 1) % n;
        cur.dispatchEvent(new Event("change", { bubbles: true }));
      }
    } else cur.click();
    buzz(true);
  }
  // on-screen keyboard shortcuts, console convention: X erases, Y types a space
  const osk = !!navScope?.hasAttribute("data-osk");
  if (osk && edge(squareButton)) { padKeyboardKey("backspace"); buzz(false); }
  if (osk && edge(triangleButton)) { padKeyboardKey("space"); buzz(false); }
  const backTarget = padBackTarget(els);
  const startTarget = padStartTarget(els);
  if (circleEdge()) { // Circle/B = back / leave
    if (isTextEntry(cur)) {
      navTypingEl = null;
      navSuppressRestoreUntil = now + 300;
      cur.blur();
      buzz(false);
    } else if (backTarget) {
      backTarget.click();
      buzz(true);
    }
  }
  if (edge(9) && startTarget) { // START/Options = launch (start game / play) no matter the focus
    startTarget.click();
    buzz(true);
  }
  // prompt bar: only what actually does something on THIS screen
  const g = padGlyphs(gp);
  const hints: [string, string][] = [];
  const curAct = cur instanceof HTMLInputElement && cur.type === "range" ? null
    : cur instanceof HTMLSelectElement ? "Change"
    : isTextEntry(cur) ? "Type"
    : cur instanceof HTMLInputElement && cur.type === "checkbox" ? "Toggle"
    : "Select";
  if (cur instanceof HTMLInputElement && cur.type === "range") hints.push(["◀▶", "Adjust"]);
  else if (curAct) hints.push([g.confirm, curAct]);
  if (osk) hints.push([g.x, "Erase"], [g.y, "Space"]);
  if (backTarget) hints.push([g.back, backTarget.hasAttribute("data-pad-back") ? (backTarget.getAttribute("data-pad-back") || "Back") : "Back"]);
  if (startTarget) hints.push([g.start, osk ? "Enter" : "Start"]);
  if (modeScope) hints.push([`${g.lb}/${g.rb}`, "Mode"]);
  if (mapScope) hints.push(["✚◀▶", "Map"]);
  // only once the pad is actually driving — an idle pad on the desk must not
  // put a controller legend on a mouse player's menu
  setPadHints(padDebug || !padNavActive ? null : hints);
  navPrev = gp.buttons.map((b) => b.pressed);
};
padMenuLoop();

// ---- AUTO-UPDATE WATCHDOG: the game is a long-lived SPA, so deploys were
// invisible until a manual hard reload — every "still broken / nothing
// changed" report traced back to an old bundle. Poll the served index for a
// new asset hash; reload instantly on plain menu screens, defer while in a
// match or a room lobby and apply the moment the player is back out.
{
  const myBundle = (document.querySelector('script[src*="assets/index-"]') as HTMLScriptElement | null)?.src ?? "";
  let updatePending = false;
  const safeToReload = (): boolean =>
    !document.querySelector(".hud") && !document.querySelector(".lobby-screen") && !document.querySelector(".dropin-screen");
  window.setInterval(() => {
    if (updatePending) {
      if (safeToReload()) location.reload();
      return;
    }
    if (!myBundle) return;
    void fetch("/index.html", { cache: "no-store" })
      .then((r) => r.text())
      .then((html) => {
        const m = html.match(/assets\/index-[A-Za-z0-9_-]+\.js/);
        if (!m || myBundle.includes(m[0])) return;
        updatePending = true;
        if (safeToReload()) location.reload();
      })
      .catch(() => { /* offline — retry next tick */ });
  }, 40000);
}

// Escape backs out of menu screens (same as the gamepad B button). In a
// match the HUD exists and ESC belongs to the pause menu — skip it there.
window.addEventListener("keydown", (e) => {
  if (e.code !== "Escape") return;
  if (document.querySelector(".hud")) return; // in-game: pause handles ESC
  padBackTarget(menuFocusables())?.click();
});

// TV remote menu navigation. The pad-nav loop above is gamepad-only, but a TV
// remote sends ARROW KEYS, not gamepad input — so without this the remote can't
// move through the menu. On the TV app only, drive the same focus ring from the
// arrow keys (Enter already clicks a focused button in the browser). Desktop and
// tablet players are untouched.
window.addEventListener("keydown", (e) => {
  if (!isTvApp()) return;
  if (document.querySelector(".hud")) return;         // not during a match
  if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
  const els = menuFocusables();
  if (!els.length) return;
  const cur = document.activeElement as HTMLElement | null;
  const inText =
    (cur instanceof HTMLInputElement &&
      /^(text|email|password|search|number|url|tel)$/.test(cur.type || "text")) ||
    cur instanceof HTMLTextAreaElement;
  const idx = cur ? els.indexOf(cur) : -1;

  const vertical = e.key === "ArrowDown" || e.key === "ArrowUp";
  // inside a text field, left/right stay as cursor movement
  if (!vertical && inText) return;
  e.preventDefault();
  const dx = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
  const dy = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
  // same geometric walk as the pad (grids/rows), DOM-order wrap at the edge
  const next = idx < 0 ? padDefaultTarget(els)
    : spatialPick(els, els[idx], dx, dy) ?? (vertical ? els[dy > 0 ? 0 : els.length - 1] : null);
  next?.focus();
  next?.scrollIntoView({ block: "nearest", inline: "nearest" });
});

// Rotating 3D character behind the main menu. Recreated on each return so it
// reflects the latest cosmetics/skin; torn down whenever a match/practice or
// a full-screen sub-menu takes over the canvas.
let menuBg: MenuBackground | null = null;
// the one active in-room session — disposed before entering another room so
// accepting an invite mid-room never leaves two sessions fighting the canvas
let activeSession: RoomSession | null = null;
// The in-room stack (RoomSession → match.ts and its whole render chain) is the
// biggest cluster of app code and none of it is needed to paint the menu, so
// it lives in its own code-split chunk. The fetch starts at boot: it downloads
// in parallel without blocking first paint, and by the time a human clicks
// Play it is resolved — an import() of an already-loaded module settles in a
// microtask, before the event loop can deliver any queued websocket message,
// so joining drops nothing. Every join path funnels through enterRoom below.
const roomSessionLoad = import("./game/roomSession.js");
roomSessionLoad.catch(() => { /* surfaced by enterRoom's catch when it matters */ });
function enterRoom(room: import("colyseus.js").Room, onLeave: () => void = toPlay): void {
  void roomSessionLoad
    .then(({ RoomSession }) => {
      activeSession?.dispose();
      disposeMenuBg(); // a match owns the shared renderer — free the backdrop scene
      clearInvite();
      clearWelcome(); // launching a game dismisses the "take the training" nudge
      activeSession = new RoomSession(canvas, room, () => { activeSession = null; onLeave(); });
    })
    .catch((err) => {
      // The match chunk failed to load (network drop, or a redeploy pruned the
      // old hashed asset). Don't strand a headless room connection — leave it
      // and fall back like a normal session end (the menu is in the entry
      // chunk, so it still works).
      reportClientError(`match chunk load failed: ${String(err)}`);
      try { room.leave(); } catch (e) { /* already gone */ }
      onLeave();
    });
}
// The backdrop shows the player's own avatar — rebuild only when that changed.
let menuBgSig = "";
const menuBgSignature = (): string => JSON.stringify([api.account?.cosmetics ?? null, api.account?.skinUrl?.length ?? 0, api.account?.skinUrl?.slice(-64) ?? ""]);
function startMenuBg(): void {
  // The TV app skips the animated 3D menu backdrop (a full scene: rotating
  // avatar + dais + grid). It's the heaviest thing on the menu and the CSS dark
  // background stands in fine on a TV. Desktop/tablet are unaffected.
  if (isTvApp()) return;
  const sig = menuBgSignature();
  if (menuBg && sig === menuBgSig) { menuBg.start(); return; } // parked → resume instantly
  disposeMenuBg();
  menuBgSig = sig;
  menuBg = new MenuBackground(canvas);
  menuBg.start();
}
/** Menu sub-screens PARK the backdrop (keep the scene, stop drawing) so the
 *  way back to the main menu is instant. Matches dispose it: disposeMenuBg. */
function stopMenuBg(): void {
  menuBg?.pause();
}
function disposeMenuBg(): void {
  menuBg?.stop();
  menuBg = null;
}

function cleanupActiveRuntimeSession(): void {
  endLocalSession(); // couch seats: leave their guest sessions + free the pads
  activeSession?.dispose({ detachVoice: true });
  activeSession = null;
  disposeMenuBg();
  stopFriendEvents();
  clearInvite();
  clearWelcome();
}

window.addEventListener("pagehide", () => {
  cleanupActiveRuntimeSession();
});

// Browser autoplay policy: audio can't start until the first user gesture.
// We queue the menu track and kick it off on the first click/keydown.
// Kick audio on every gesture (idempotent) — autoplay may reject the first
// attempt, so we keep nudging until the track actually plays.
function unlockAudio(): void {
  audio.onGesture();
  audio.voiceEnabled = loadBehavior().voiceAnnounce !== false; // persisted setting
}
for (const ev of ["pointerdown", "mousedown", "keydown", "touchstart", "click"] as const) {
  window.addEventListener(ev, unlockAudio);
}

function toMenu(): void {
  audio.playMusic("menu");
  startFriendEvents(); // idempotent — opens the invite/presence stream once
  startMenuBg(); // rotating character behind the menu (reflects latest cosmetics)
  showScreen(
    menuScreen({
      // close the presence stream too — leaving it open kept streaming the
      // previous account's events (and held it "online") until reload
      onLogout: () => { stopFriendEvents(); toAuth(); },
      onPractice: () => {
        // PRACTICE = the REAL game engine: a private no-timer solo room on the
        // Practice Field: 4 planted target dummies + 4 wandering bots. Everything behaves
        // exactly like a match (jetpack, parachute, imperion, vehicles,
        // dragon, HUD) because it IS a match — no hand-rolled sandbox drift.
        stopMenuBg();
        void createSoloRoom({ mapId: DEFAULT_MAP_ID, mode: "gungame", bots: 8, difficulty: "easy", roundTimeS: 0, passiveBots: true })
          .then((room) => enterRoom(room, toMenu))
          .catch(() => toMenu());
      },
      onTutorial: () => { void runTutorialStage(0); },
      onPlay: toPlay,
      onCosmetics: toCosmetics,
      onMapEditor: () => { stopMenuBg(); showScreen(editorScreen({ onBack: toMenu })); },
      onSettings: () => showScreen(settingsScreen(toMenu)),
      onProfile: () => { stopMenuBg(); showScreen(profileScreen(toMenu)); },
      onLeaderboard: () => { stopMenuBg(); showScreen(leaderboardScreen(toMenu)); },
      onFriends: () => { stopMenuBg(); showScreen(friendsScreen(toMenu)); },
      onAdmin: () => { stopMenuBg(); showScreen(adminScreen(toMenu)); },
    }),
  );
  // FIRST LAUNCH: quick setup + PLAY NOW / training (once per device)
  if (shouldWelcome()) {
    uiRoot().append(welcomeModal({ onPlayNow: playNow, onTraining: () => { void runTutorialStage(0); } }));
  }
}

/** PLAY NOW: Gun Game vs a handful of bots on a compact map, no setup. The
 *  bots start gentle and ramp over the first matches on this device, so a
 *  newcomer's first games are winnable (owner 2026-10-28). */
function playNow(): void {
  let n = 0;
  try { n = Number(localStorage.getItem("neonpurge.quickplays")) || 0; localStorage.setItem("neonpurge.quickplays", String(n + 1)); } catch { /* private mode */ }
  const difficulty = n < 2 ? "veryeasy" : n < 5 ? "easy" : "medium";
  stopMenuBg();
  void createSoloRoom({ mapId: "grid_arena", mode: "gungame", bots: 5, difficulty, roundTimeS: 600 })
    .then((room) => enterRoom(room, toMenu))
    .catch(() => toMenu());
}

async function runTutorialStage(n: number): Promise<void> {
  if (n >= TUTORIAL_STAGES.length) {
    setTutorialStage(-1);
    toMenu();
    return;
  }
  setTutorialStage(n);
  stopMenuBg();
  const cfg = TUTORIAL_STAGES[n];
  try {
    const room = await createSoloRoom({
      mapId: cfg.mapId, bots: cfg.bots, difficulty: "tutorial",
      roundTimeS: 0, monsters: false,
    });
    enterRoom(room, () => {
      // ONLY an explicit stage completion advances; any other leave (Esc →
      // Leave Match, connection loss) aborts the campaign back to the menu.
      // (The old `tutorialStage() === n` check was true in BOTH cases, so
      // pressing Leave dragged you into the next tutorial stage.)
      if (consumeStageComplete()) {
        void runTutorialStage(n + 1);
      } else {
        setTutorialStage(-1);
        toMenu();
      }
    });
  } catch {
    setTutorialStage(-1);
    toMenu();
  }
}

function toCosmetics(): void {
  stopMenuBg();
  showScreen(
    cosmeticsScreen(toMenu, () => showScreen(skinEditor(toCosmetics))),
  );
}

function toPlay(): void {
  stopMenuBg();
  audio.playMusic("menu"); // lobby stays on menu music until a match starts
  showScreen(
    playScreen({
      onBack: toMenu,
      onJoined: (room) => enterRoom(room),
      onLocal: toLocal,
    }),
  );
}

/** MULTIPLAYER LOCAL: the couch lobby (claim controllers, pick a mode/map,
 *  start split-screen). Leaving the match tears the seat sessions down and
 *  lands back here, ready for a rematch. */
function toLocal(): void {
  stopMenuBg();
  audio.playMusic("menu");
  showScreen(
    localScreen({
      onBack: toPlay,
      onJoined: (room) => enterRoom(room, () => { endLocalSession(); toLocal(); }),
    }),
  );
}

function toAuth(): void {
  stopMenuBg();
  showScreen(authScreen(afterAuth));
}

// Runs once auth clears (login OR guest). On a device that has never connected
// before, offer the tutorial via a one-shot welcome popup (localStorage-gated).
function afterAuth(): void {
  toMenu();
  let seen = false;
  try { seen = localStorage.getItem("np_welcomed") === "1"; } catch (e) { /* private mode / disabled */ }
  if (seen) return;
  // A RETURNING player (any match history) never gets the "take the training"
  // nudge — it's only for genuine first-timers. Gate on real stats, not just the
  // per-device flag (a cleared cache / new device shouldn't re-nag a veteran).
  const offer = (): void => {
    try { localStorage.setItem("np_welcomed", "1"); } catch (e) { /* ignore */ }
    if (activeSession) return; // they already launched a game — don't pop over it
    showWelcome(() => { void runTutorialStage(0); }, () => {/* "No" — stay on the menu */});
  };
  void api.getStats()
    .then((s) => {
      const played = !!s && ((s.matches ?? 0) > 0 || (s.kills ?? 0) > 0 || (s.deaths ?? 0) > 0 || (s.playtimeS ?? 0) > 20);
      if (played) { try { localStorage.setItem("np_welcomed", "1"); } catch (e) { /* ignore */ } return; }
      offer();
    })
    .catch(offer); // no stats (fresh guest) → treat as new
}

// ONE global invite handler for the whole app lifetime — shows a popup over
// any screen; accepting switches rooms cleanly via enterRoom.
onFriendEvent((e) => {
  if (e.type === "invite") {
    showInvite(
      e.from,
      () => { void joinById(e.roomId).then((room) => enterRoom(room)).catch(() => toMenu()); },
      () => {/* declined — nothing to do */},
    );
  } else if (e.type === "friend_request") {
    showFriendRequest(
      e.username,
      () => { void friends.accept(e.id).catch(() => {/* ignore */}); },
      () => { void friends.remove(e.id).catch(() => {/* ignore */}); },
    );
  }
});

async function boot(): Promise<void> {
  const bootUi = createBootOverlay();
  try {
    bootUi.set(0.08, "CLOSING OLD SESSION");
    cleanupActiveRuntimeSession();
    bootUi.set(0.18, "CLEARING OLD FILES");
    await clearBrowserCaches().catch(() => { /* non-critical */ });
    bootUi.set(0.36, "CHECKING LIVE BUILD");
    await validateFreshLaunch().catch(() => { /* offline: keep current launch */ });
    bootUi.set(0.44, "PRELOADING GAME");
    await preloadGameAssets(bootUi);
    bootUi.set(0.88, "RESTORING SESSION");
    const account = await api.restore();
    bootUi.set(1, "READY");
    window.setTimeout(() => {
      bootUi.done();
      if (account) { startFriendEvents(); toMenu(); }
      else toAuth();
      // The menu is interactive now — warm the remaining mid-match texture
      // families on idle time (they were pulled off the blocking boot path).
      void warmDeferredTextures();
      installXpCardDemo(); // debug: window.__xpCardDemo for screenshots (opt-in)
    }, 120);
  } catch (err) {
    reportClientError(`boot failed: ${String(err)}`, (err as Error)?.stack);
    bootUi.done();
    toAuth();
  }
}

void boot();
